import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useScroll } from "@/lib/scroll";
import { useCapabilities } from "@/lib/device";
import { Magnetic } from "@/components/primitives";
import HeroReel from "@/components/HeroReel";
import MediaImage from "@/components/MediaImage";
import { heroReel } from "@/config/media";
import { markHeroDone } from "@/lib/livingRegistry";

const HEADLINE = ["Films that sell rooms.", "Without a film crew."];

const LINE_SPRING = { type: "spring", stiffness: 72, damping: 20, mass: 1 };

export default function Hero({ introDone }) {
  const { scrollTo } = useScroll();
  const caps = useCapabilities();
  const ref = useRef(null);
  const [clip, setClip] = useState(0);
  const stageInView = useInView(ref, { margin: "20% 0px" });

  const show = introDone;
  // Hero film runs on every device the user hasn't opted out of motion on.
  // Poster paints first; the video attaches after the intro and fades in on
  // canplay. Mobile gets the reel too — this is the one background film the
  // page is built around.
  const showReel = caps.ready && caps.heroFilm;

  return (
    <section
      id="top"
      ref={ref}
      className="on-dark relative flex min-h-[100svh] flex-col justify-center px-6 pb-20 pt-28 sm:px-10"
      data-testid="hero"
    >
      {/* Full-bleed film, fixed behind the page — content scrolls over it. */}
      <div
        className="fixed inset-0 z-0"
        style={{ visibility: !caps.ready || stageInView ? "visible" : "hidden" }}
        data-testid="hero-film-layer"
      >
        <MediaImage id={heroReel[0].poster} eager sizes="100vw" className="h-full w-full object-cover" />
        {showReel && introDone && (
          <HeroReel paused={!introDone || !stageInView} onClipChange={setClip} onFirstPlay={markHeroDone} />
        )}
        {/* Layered scrims — legibility must hold across every frame in the reel,
            including the brightest sunset and daylight passages. */}
        {/* top: covers the nav no matter how bright the sky is */}
        <div className="absolute inset-x-0 top-0 h-[42%] bg-gradient-to-b from-ink/85 via-ink/45 to-transparent" aria-hidden="true" />
        {/* bottom: protects meta strip + scroll cue */}
        <div className="absolute inset-x-0 bottom-0 h-[45%] bg-gradient-to-t from-ink/88 via-ink/45 to-transparent" aria-hidden="true" />
        {/* left column: the entire copy block sits here, needs to be readable
            even when the frame's brightest area is on the left */}
        <div className="absolute inset-y-0 left-0 w-[72%] bg-gradient-to-r from-ink/78 via-ink/40 to-transparent" aria-hidden="true" />
        {/* a global tint takes the highlights down a stop */}
        <div className="absolute inset-0 bg-ink/20" aria-hidden="true" />
        {/* Filmic vignette — corners darken by ~14%, headline stays clear.
            This is the tell between "video on a webpage" and "a piece of film". */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(120% 90% at 50% 45%, transparent 40%, rgba(10,8,6,0.35) 78%, rgba(10,8,6,0.6) 100%)",
          }}
          aria-hidden="true"
        />
        {/* Film-layer grain — only on top of the video, distinct from the
            page-wide grain overlay. Subtle animated shift keeps it alive. */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay animate-grain"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='180' height='180'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
            backgroundSize: "180px 180px",
          }}
          aria-hidden="true"
        />
      </div>

      {/* capture-metadata strip */}
      {showReel && (
        <motion.div
          className="pointer-events-none absolute left-8 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-1.5 2xl:flex"
          initial={{ opacity: 0 }}
          animate={show ? { opacity: 0.5 } : {}}
          transition={{ delay: 1.5, duration: 0.9 }}
          data-testid="hero-meta-strip"
          aria-hidden="true"
        >
          <motion.span
            className="mb-1 h-1.5 w-1.5 rounded-full bg-brass"
            animate={{ opacity: [0.2, 1, 0.2] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          />
          {heroReel[clip].meta.map((line) => (
            <span
              key={line}
              className="font-mono text-[9px] uppercase tracking-[0.28em] text-primaryText"
            >
              {line}
            </span>
          ))}
        </motion.div>
      )}

      <div className="relative z-10 mx-auto w-full max-w-[1280px]">
        <div className="max-w-3xl">
          <div className="mb-8 flex items-center gap-4">
            <motion.span
              className="block h-px w-12 origin-left bg-brass"
              initial={{ scaleX: 0 }}
              animate={show ? { scaleX: 1 } : {}}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            />
            <motion.span
              className="eyebrow"
              initial={{ opacity: 0 }}
              animate={show ? { opacity: 1 } : {}}
              transition={{ delay: 0.25, duration: 0.8 }}
            >
              For luxury villas, hotels &amp; resorts
            </motion.span>
          </div>

          <h1
            className="display text-primaryText"
            style={{
              fontSize: "clamp(2.75rem, 6.2vw, 5.5rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
              fontWeight: 300,
            }}
          >
            {HEADLINE.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.14em]">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={show ? { y: "0%" } : {}}
                  transition={{ ...LINE_SPRING, delay: 0.18 + i * 0.11 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="mt-8 max-w-md text-base leading-relaxed text-mutedText sm:text-lg"
            initial={{ opacity: 0, y: 22 }}
            animate={show ? { opacity: 1, y: 0 } : {}}
            transition={{ type: "spring", stiffness: 64, damping: 19, delay: 0.75 }}
          >
            We take the photography your property already owns and turn it into
            motion for your website, your social channels and every booking
            surface that rewards it. Nothing is shot on site. Not a single
            guest is disturbed.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col gap-4 sm:flex-row"
            initial={{ opacity: 0, y: 22 }}
            animate={show ? { opacity: 1, y: 0 } : {}}
            transition={{ type: "spring", stiffness: 64, damping: 19, delay: 0.9 }}
          >
            <Magnetic>
              <button
                onClick={() => scrollTo("#enquiry")}
                className="btn-sweep w-full rounded-full bg-brass px-8 py-4 text-sm font-medium tracking-wide text-ink transition-colors duration-300 hover:bg-[#c9aa6c] sm:w-auto"
                data-testid="hero-cta-primary"
              >
                See your property move
              </button>
            </Magnetic>
            <Magnetic>
              <button
                onClick={() => scrollTo("#living-frame")}
                className="btn-sweep w-full rounded-full border border-brass/50 px-8 py-4 text-sm tracking-wide text-primaryText transition-colors duration-300 hover:border-brass sm:w-auto"
                data-testid="hero-cta-secondary"
              >
                Watch the difference
              </button>
            </Magnetic>
          </motion.div>
        </div>
      </div>

      {/* film meta row */}
      <motion.div
        className="absolute inset-x-6 bottom-8 z-10 flex items-end justify-between sm:inset-x-10"
        initial={{ opacity: 0 }}
        animate={show ? { opacity: 1 } : {}}
        transition={{ delay: 1.3, duration: 0.9 }}
      >
        <span className="hidden font-mono text-[10px] tracking-[0.3em] text-mutedText sm:block" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.9)" }}>
          DEMONSTRATION REEL · 24 FPS
        </span>
        <div className="flex flex-col items-center gap-2" aria-hidden="true">
          <span className="font-mono text-[10px] tracking-[0.3em] text-mutedText" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.9)" }}>
            SCROLL
          </span>
          <motion.span
            className="h-10 w-px bg-brass/60"
            animate={{ scaleY: [0.3, 1, 0.3], opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            style={{ transformOrigin: "top" }}
          />
        </div>
        <span className="hidden font-mono text-[10px] tracking-[0.3em] text-mutedText sm:block" style={{ textShadow: "0 1px 8px rgba(10,8,6,0.9)" }}>
          BALI · MALDIVES · THE ALPS
        </span>
      </motion.div>
    </section>
  );
}
